import type { Model } from './codec';
import { combine, type Route } from './combine';
import { decide, type Decision } from './decide';
import { routerModel } from './model';
import { ROUTER_META } from './model.meta';
import { routerNormalize } from './features';
import { probsOf } from './predict';
import { ruleIntent } from './rules';
import { INTENT_IDS, domainOf, type IntentId } from './taxonomy';

/** Longer than anything the stage lets through; the router reads no more than this. */
const MAX_CHARS = 400;

/** A sentence this short has nothing for the model to read: a keyword is all there is to go on. */
const BARE_WORDS = 2;

/**
 * Does the sentence hold a word to read at all? Not a single letter ("x", "q": the short forms would spell them out as
 * "por" and "que" for the model), and not only emoji or numbers.
 */
function hasSomethingToRead(said: string): boolean {
  return /\p{L}{2,}/u.test(said);
}

/**
 * Which situation a question is, given the model (or none yet). The model reads the whole sentence and
 * the keywords back it up (see `combine`); without the model only the keywords answer, and a question
 * they do not know is "fuera", which gets the honest "todavía no". It never throws.
 */
export function routeWith(model: Model | undefined, text: string): Route {
  const said = text.slice(0, MAX_CHARS);
  const keyword = ruleIntent(said);
  if (!model || !hasSomethingToRead(said)) {
    return keyword
      ? { intent: keyword, domain: domainOf(keyword), confidence: 1, via: 'rule' }
      : { intent: 'fuera', domain: 'fuera', confidence: 0, via: 'unknown' };
  }
  return explainRoute(model, text).route;
}

/** Everything the router saw for a sentence: the keyword, what the model alone decided, and the route that came of both. */
export function explainRoute(model: Model, text: string): { keyword: IntentId | null; model: Decision; route: Route } {
  const said = text.slice(0, MAX_CHARS);
  const probs = probsOf(model, said, { intent: ROUTER_META.intentTemperature, domain: ROUTER_META.domainTemperature });
  const decision = decide(probs, ROUTER_META.params);
  const intent = ruleIntent(said);
  const keyword = intent ? { intent, prob: probs.intent[INTENT_IDS.indexOf(intent)] ?? 0 } : null;
  const { trust, topicTrust, veto, vetoBest, fueraTrust } = ROUTER_META;
  const bare = routerNormalize(said).split(' ').filter(Boolean).length <= BARE_WORDS;
  // With one or two words the keyword is the whole sentence: the model may not veto it or refuse it.
  const policy = bare ? { trust, topicTrust, veto: 0, vetoBest, fueraTrust: 1.01 } : { trust, topicTrust, veto, vetoBest, fueraTrust };
  return { keyword: intent, model: decision, route: combine(keyword, decision, policy) };
}

export function routeQuestion(text: string): Route {
  return routeWith(routerModel(), text);
}
